import { AfterViewInit, Directive, ElementRef, inject, OnDestroy } from '@angular/core';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Keyboard use of a modal dialog of the Documents window: focus moves into it when it opens (the element marked
 * data-autofocus, else the first field), Tab stays inside, and the focus goes back where it was when it closes.
 */
@Directive({
  selector: '[iuDialogFocus]',
  standalone: true,
  host: { '(keydown.tab)': 'onTab($event)', '(keydown.shift.tab)': 'onTab($event)' },
})
export class DialogFocusDirective implements AfterViewInit, OnDestroy {
  readonly #host = inject(ElementRef<HTMLElement>);
  readonly #previous = document.activeElement as HTMLElement | null;

  ngAfterViewInit(): void {
    setTimeout(() => {
      const el: HTMLElement = this.#host.nativeElement;
      if (el.contains(document.activeElement)) return;
      (el.querySelector<HTMLElement>('[data-autofocus]') ?? this.#focusable()[0] ?? el).focus();
    });
  }

  ngOnDestroy(): void {
    // back to the control that opened the dialog, when it is still on screen
    const prev = this.#previous;
    if (prev?.isConnected) setTimeout(() => prev.focus());
  }

  onTab(event: Event): void {
    const e = event as KeyboardEvent;
    const items = this.#focusable();
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  #focusable(): HTMLElement[] {
    return Array.from((this.#host.nativeElement as HTMLElement).querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (x) => x.offsetParent !== null || x === document.activeElement
    );
  }
}
